import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TablePagination,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";

const RouteManagement = () => {
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    routeName: "",
    startingPoint: "",
    endPoint: "",
    driverId: "",
  });
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchRoutes();
    fetchDrivers();
  }, []);

  useEffect(() => {
    setFilteredRoutes(
      routes.filter((route) =>
        route.routeName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [routes, searchQuery]);

  const fetchRoutes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/routes`);
      if (response.ok) {
        const data = await response.json();

        setRoutes(data);
      } else {
        console.error("Failed to fetch routes");
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/drivers`);
      if (response.ok) {
        const data = await response.json();
        setDrivers(data);
      } else {
        console.error("Failed to fetch drivers");
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const handleOpen = (route = null) => {
    console.log("route is ", route);
    if (route) {
      setIsEditing(true);
      setFormData({
        _id: route._id,
        routeName: route.routeName,
        startingPoint: route.startingPoint,
        endPoint: route.endPoint,
        driverId: route.driver ? route.driver._id : "", // Extract driverId correctly
      });
    } else {
      setIsEditing(false);
      setFormData({
        routeName: "",
        startingPoint: "",
        endPoint: "",
        driverId: "",
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddRoute = async (updatedFormData) => {
    const endpoint = `${process.env.REACT_APP_API_URL}/route`;
    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updatedFormData),
    };

    try {
      const response = await fetch(endpoint, options);
      if (response.ok) {
        const newRoute = await response.json();
        setRoutes((prev) => [...prev, newRoute]);
        fetchRoutes();
        fetchDrivers();
        handleClose();
      } else {
        console.error("Failed to add route");
      }
    } catch (error) {
      console.error("Error during route addition:", error);
    }
  };

  const handleUpdateRoute = async (updatedFormData) => {
    const endpoint = `${process.env.REACT_APP_API_URL}/route/${updatedFormData._id}`;
    const options = {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updatedFormData),
    };

    try {
      const response = await fetch(endpoint, options);
      if (response.ok) {
        const updatedRoute = await response.json();
        setRoutes((prev) =>
          prev.map((route) =>
            route._id === formData._id ? updatedRoute : route
          )
        );
        fetchRoutes();
        fetchDrivers();
        handleClose();
      } else {
        console.error("Failed to update route");
      }
    } catch (error) {
      console.error("Error during route update:", error);
    }
  };

  const handleSubmit = () => {
    const updatedFormData = {
      ...formData,
      driverId: formData.driverId || "", // Send null if no driver selected
    };
    if (isEditing) {
      handleUpdateRoute(updatedFormData);
    } else {
      handleAddRoute(updatedFormData);
    }
  };

  const handleDeleteConfirm = (id) => {
    setSelectedRouteId(id);
    setConfirmOpen(true);
  };

  const handleDeleteCancel = () => {
    setConfirmOpen(false);
    setSelectedRouteId(null);
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/route/${selectedRouteId}`,
        {
          method: "DELETE",
        }
      );
      if (response.ok) {
        await response.json();
        setConfirmOpen(false);
        setSelectedRouteId(null);
        fetchRoutes();
        fetchDrivers();
      } else {
        console.error("Failed to delete route");
      }
    } catch (error) {
      console.error("Error deleting the route:", error);
    }
  };
  const shopPagination = filteredRoutes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box padding={3}>
      <h2>Route Management</h2>
      <Box display="flex" justifyContent="space-between" marginBottom={2}>
        <Box sx={{ width: "50%" }}>
          <TextField
            label="Search Routes"
            variant="outlined"
            fullWidth
            margin="normal"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search Routes"
          />
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpen()}
          sx={{ height: 50 }}
        >
          Add Route
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Route Name
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Starting Point
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                End Point
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Driver
              </TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shopPagination.map((route) => (
              <TableRow key={route.id}>
                <TableCell>
                  <Tooltip title={route.routeName}>
                    <Typography
                      noWrap
                      sx={{
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {route.routeName}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{route.startingPoint}</TableCell>
                <TableCell>{route.endPoint}</TableCell>
                <TableCell>{route.driver?.fullName || "Unassigned"}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleOpen(route)}
                    style={{ marginRight: "10px" }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleDeleteConfirm(route._id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{isEditing ? "Edit Route" : "Add Route"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="routeName"
            label="Route Name"
            type="text"
            fullWidth
            value={formData.routeName}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="startingPoint"
            label="Starting Point"
            type="text"
            fullWidth
            value={formData.startingPoint}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="endPoint"
            label="End Point"
            type="text"
            fullWidth
            value={formData.endPoint}
            onChange={handleChange}
          />
          <TextField
            select
            margin="dense"
            name="driverId"
            label="Driver"
            fullWidth
            value={formData.driverId || ""}
            onChange={handleChange}
          >
            {drivers.map((driver) => (
              <MenuItem key={driver._id} value={driver._id}>
                {driver.fullName}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary">
            {isEditing ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this route?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDelete();
            }}
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <TablePagination
        component="div"
        count={routes.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default RouteManagement;
